import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    Grid,
    CircularProgress
} from '@mui/material';
import { Organization, OrganizationFormData } from '../../types/organization';
import api from '../../services/api';

interface OrganizationModalProps {
    open: boolean;
    onClose: () => void;
    onSave: (org: Organization) => void;
    organization?: Organization | null;
}

const OrganizationModal: React.FC<OrganizationModalProps> = ({ open, onClose, onSave, organization }) => {
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState<OrganizationFormData>({
        name: '',
        subdomain: '',
        logoUrl: '',
        theme: {
            primaryColor: '#3b82f6',
            secondaryColor: '#1e40af'
        }
    });
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (organization) {
            setFormData({
                name: organization.name,
                subdomain: organization.subdomain,
                logoUrl: organization.logoUrl || '',
                theme: {
                    primaryColor: organization.theme?.primaryColor || '#3b82f6',
                    secondaryColor: organization.theme?.secondaryColor || '#1e40af'
                }
            });
        } else {
            setFormData({
                name: '',
                subdomain: '',
                logoUrl: '',
                theme: {
                    primaryColor: '#3b82f6',
                    secondaryColor: '#1e40af'
                }
            });
        }
        setError(null);
    }, [organization, open]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        if (name.startsWith('theme.')) {
            const themeField = name.split('.')[1];
            setFormData(prev => ({
                ...prev,
                theme: {
                    ...prev.theme,
                    [themeField]: value
                }
            }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async () => {
        setLoading(true);
        setError(null);
        try {
            let res;
            if (organization) {
                res = await api.put(`/api/organizations/${organization._id}`, formData);
            } else {
                res = await api.post('/api/organizations', formData);
            }
            onSave(res.data);
            onClose();
        } catch (err: any) {
            console.error('Error saving organization', err);
            setError(err.response?.data?.message || 'Une erreur est survenue');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {organization ? 'Modifier l\'organisme' : 'Nouvel organisme'}
            </DialogTitle>
            <DialogContent dividers>
                {error && (
                    <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                        {error}
                    </Typography>
                )}
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                        <TextField
                            label="Nom de l'organisme"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            fullWidth
                            required
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <TextField
                            label="Sous-domaine"
                            name="subdomain"
                            value={formData.subdomain}
                            onChange={handleChange}
                            fullWidth
                            required
                            helperText="Utilisé pour l'URL (ex: mon-ecole.lms.com)"
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <TextField
                            label="URL du Logo"
                            name="logoUrl"
                            value={formData.logoUrl}
                            onChange={handleChange}
                            fullWidth
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, mt: 1 }}>Thème</Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                        <TextField
                            label="Couleur Principale"
                            name="theme.primaryColor"
                            value={formData.theme.primaryColor}
                            onChange={handleChange}
                            fullWidth
                            type="color"
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                        <TextField
                            label="Couleur Secondaire"
                            name="theme.secondaryColor"
                            value={formData.theme.secondaryColor}
                            onChange={handleChange}
                            fullWidth
                            type="color"
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>Annuler</Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    color="primary"
                    disabled={loading || !formData.name || !formData.subdomain}
                >
                    {loading ? <CircularProgress size={24} /> : 'Enregistrer'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default OrganizationModal;
